/** Page badges - onglet Enrôler (associer un badge) et onglet Rechercher (identifier un badge). */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Check, Search, Trash2, UserCheck, XCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { useEmployees, useAssignBadge, useRemoveBadge } from '../hooks/useEmployees';
import { useLookupBadge, type BadgeLookupResult } from '../hooks/useBadges';
import { normalizeBadgeUid } from '../lib/nfc';
import { cn } from '../lib/utils';

type Tab = 'enroll' | 'lookup';

export function BadgeEnrollPage() {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('employee');

  // Onglet actif
  const [tab, setTab] = useState<Tab>('enroll');

  return (
    <div>
      <Header title="Badges" />

      {/* Onglets */}
      <div className="flex gap-1 border-b border-gray-200 mb-5">
        <TabButton active={tab === 'enroll'} onClick={() => setTab('enroll')}>
          <CreditCard size={14} /> Enrôler un badge
        </TabButton>
        <TabButton active={tab === 'lookup'} onClick={() => setTab('lookup')}>
          <Search size={14} /> Rechercher un badge
        </TabButton>
      </div>

      <div className="max-w-xl">
        {tab === 'enroll' ? <EnrollSection preselectedId={preselectedId} /> : <LookupSection />}
      </div>
    </div>
  );
}

// --- Onglet ---

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors',
        '-mb-px border-b-2',
        active
          ? 'text-accent border-accent'
          : 'text-gray-500 border-transparent hover:text-gray-700'
      )}
    >
      {children}
    </button>
  );
}

// --- Section ENRÔLEMENT ---

function EnrollSection({ preselectedId }: { preselectedId: string | null }) {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(preselectedId || '');
  const [nfcUid, setNfcUid] = useState('');
  const [manualUid, setManualUid] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [nfcCaptureActive, setNfcCaptureActive] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: employeesData } = useEmployees({ per_page: 100, active_only: true });
  const assignBadge = useAssignBadge();

  // Focus sur l'input NFC uniquement quand la capture est active
  useEffect(() => {
    if (!nfcCaptureActive) return;
    inputRef.current?.focus();
    const interval = setInterval(() => inputRef.current?.focus(), 300);
    return () => clearInterval(interval);
  }, [nfcCaptureActive]);

  // Quand un employé est choisi, forcer la capture (le select a volé le focus)
  // + focus immédiat sur l'input caché pour que le lecteur USB soit écouté.
  useEffect(() => {
    if (!selectedEmployeeId) return;
    setNfcCaptureActive(true);
    // Le <select> ne relâche parfois pas le focus tout de suite : on laisse le
    // cycle de rendu se terminer avant de forcer le focus.
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [selectedEmployeeId]);

  // Capture touche Enter du lecteur NFC - normalise pour gérer les layouts AZERTY
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const uid = normalizeBadgeUid(nfcUid);
        if (uid.length >= 8) setNfcUid(uid);
      }
    },
    [nfcUid]
  );

  const handleAssign = async (uid: string) => {
    setError('');
    setSuccess('');

    if (!selectedEmployeeId) {
      setError('Sélectionnez un employé');
      return;
    }
    const normalized = normalizeBadgeUid(uid);
    if (normalized.length < 8) {
      setError('UID trop court');
      return;
    }

    try {
      await assignBadge.mutateAsync({
        id: Number(selectedEmployeeId),
        badge_uid: normalized,
      });
      const emp = employeesData?.items.find((e) => e.id === Number(selectedEmployeeId));
      setSuccess(`Badge ${normalized} associé à ${emp?.first_name} ${emp?.last_name}`);
      setNfcUid('');
      setManualUid('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <>
      <Card title="1. Sélectionnez l'employé" className="mb-4">
        <select
          value={selectedEmployeeId}
          onFocus={() => setNfcCaptureActive(false)}
          onBlur={() => setNfcCaptureActive(true)}
          onChange={(e) => {
            setSelectedEmployeeId(e.target.value);
            setSuccess('');
            setError('');
          }}
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                     focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
        >
          <option value="">— Choisir un employé —</option>
          {employeesData?.items.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name}
              {emp.badge_uid ? ` (badge: ${emp.badge_uid})` : ' (sans badge)'}
            </option>
          ))}
        </select>
      </Card>

      <Card
        title="2. Présentez le badge sur le lecteur USB"
        className={cn(
          'mb-4 transition-colors',
          selectedEmployeeId ? 'border-accent/40' : 'opacity-60'
        )}
      >
        <input
          ref={inputRef}
          value={nfcUid}
          onChange={(e) => setNfcUid(e.target.value)}
          onKeyDown={handleKeyDown}
          className="absolute opacity-0 pointer-events-none"
          autoComplete="off"
        />

        <div className="text-center py-6">
          <div
            className={cn(
              'w-24 h-24 mx-auto mb-4 border-2 rounded-xl flex items-center justify-center',
              selectedEmployeeId
                ? 'border-dashed border-accent/60 bg-accent-soft animate-pulse'
                : 'border-dashed border-gray-300'
            )}
          >
            <CreditCard
              size={36}
              className={selectedEmployeeId ? 'text-accent' : 'text-gray-400'}
            />
          </div>

          {!selectedEmployeeId ? (
            <p className="text-sm text-gray-400 italic">Sélectionne d'abord un employé</p>
          ) : nfcUid.length >= 8 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                UID lu : <span className="font-mono font-bold text-gray-900">{nfcUid}</span>
              </p>
              <Button onClick={() => handleAssign(nfcUid)} loading={assignBadge.isPending}>
                Associer le badge
              </Button>
            </div>
          ) : (
            <p className="text-sm text-accent font-medium">En attente de badge...</p>
          )}
        </div>
      </Card>

      <Card title="Ou saisir manuellement" className="mb-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              value={manualUid}
              onChange={(e) => setManualUid(e.target.value)}
              placeholder="04A1B2C3D4E5F6"
              onFocus={() => setNfcCaptureActive(false)}
              onBlur={() => setNfcCaptureActive(true)}
            />
          </div>
          <Button
            onClick={() => handleAssign(manualUid)}
            loading={assignBadge.isPending}
            disabled={manualUid.length < 8}
          >
            Associer
          </Button>
        </div>
      </Card>

      <FeedbackMessage success={success} error={error} />
    </>
  );
}

// --- Section RECHERCHE ---

function LookupSection() {
  const [nfcUid, setNfcUid] = useState('');
  const [manualUid, setManualUid] = useState('');
  const [result, setResult] = useState<BadgeLookupResult | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nfcCaptureActive, setNfcCaptureActive] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const lookup = useLookupBadge();
  const removeBadge = useRemoveBadge();

  useEffect(() => {
    if (!nfcCaptureActive) return;
    inputRef.current?.focus();
    const interval = setInterval(() => inputRef.current?.focus(), 300);
    return () => clearInterval(interval);
  }, [nfcCaptureActive]);

  // Recherche dès qu'un UID valide est capturé
  const runLookup = useCallback(async (uid: string) => {
    setError('');
    setSuccess('');
    setResult(null);

    const normalized = normalizeBadgeUid(uid);
    if (normalized.length < 8) {
      setError('UID trop court');
      return;
    }

    try {
      const res = await lookup.mutateAsync(normalized);
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }, [lookup]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        runLookup(nfcUid);
        setNfcUid('');
      }
    },
    [nfcUid, runLookup]
  );

  const handleRemove = async () => {
    if (!result?.employee_id) return;
    if (!window.confirm(`Dissocier le badge ${result.badge_uid} de ${result.employee_name} ?`)) return;

    try {
      await removeBadge.mutateAsync(result.employee_id);
      setSuccess(`Badge dissocié de ${result.employee_name}`);
      setResult({ ...result, found: false, employee_id: null, employee_name: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <>
      <Card title="Présentez le badge à identifier" className="mb-4">
        <input
          ref={inputRef}
          value={nfcUid}
          onChange={(e) => setNfcUid(e.target.value)}
          onKeyDown={handleKeyDown}
          className="absolute opacity-0 pointer-events-none"
          autoComplete="off"
        />

        <div className="text-center py-6">
          <div className="w-24 h-24 mx-auto mb-4 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center animate-pulse">
            <Search size={36} className="text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">
            {lookup.isPending ? 'Recherche...' : 'En attente de badge...'}
          </p>
        </div>
      </Card>

      <Card title="Ou saisir manuellement" className="mb-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              value={manualUid}
              onChange={(e) => setManualUid(e.target.value)}
              placeholder="04A1B2C3D4E5F6"
              onFocus={() => setNfcCaptureActive(false)}
              onBlur={() => setNfcCaptureActive(true)}
            />
          </div>
          <Button
            onClick={() => runLookup(manualUid)}
            loading={lookup.isPending}
            disabled={manualUid.length < 8}
          >
            Rechercher
          </Button>
        </div>
      </Card>

      {/* Résultat de la recherche */}
      {result && (
        <Card
          title="Résultat"
          className={result.found ? 'border-green-200' : 'border-amber-200'}
        >
          {result.found ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <UserCheck size={20} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{result.employee_name}</p>
                  <p className="text-xs text-gray-500">
                    {result.department || '—'} &middot; {result.site_name || '—'}
                  </p>
                  <p className="text-xs font-mono text-gray-500 mt-1">UID : {result.badge_uid}</p>
                  <div className="mt-1">
                    <Badge variant={result.is_active ? 'success' : 'danger'}>
                      {result.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <Button variant="danger" size="sm" onClick={handleRemove} loading={removeBadge.isPending}>
                  <Trash2 size={13} className="mr-1" /> Dissocier le badge
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <XCircle size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Badge non reconnu</p>
                <p className="text-xs text-gray-500">
                  Aucun employé n'est associé à ce badge.
                </p>
                <p className="text-xs font-mono text-gray-500 mt-1">UID : {result.badge_uid}</p>
              </div>
            </div>
          )}
        </Card>
      )}

      <FeedbackMessage success={success} error={error} />
    </>
  );
}

// --- Messages ---

function FeedbackMessage({ success, error }: { success: string; error: string }) {
  if (!success && !error) return null;
  return (
    <div className="mt-4">
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
          <Check size={16} /> {success}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
